/* eslint-disable @typescript-eslint/no-unused-vars */
import { ScopelyIdentity } from "@scopely/web-sdk-scopely-account";
import { LoginIdentityOperation, ScopelyId } from "@scopely/web-sdk-scopelyid";
import { AuthorizationUri } from "@scopely/web-sdk-scopelyid/dist/src/uri";
import { Dispatch, SetStateAction } from "react";
import { LoginType } from "../../../domain/Login/LoginGoogleAnalyticsHelper";
import { IDebugLogger } from "../../../utils/debugLogger";
import { useGoogleAnalyticsContext } from "../../GoogleAnalyticsProvider";
import {
	removeQueryParamsFromCurrentUrl,
	setScopelyStateToSession
} from "../helpers";

export const LOGIN_ACTION = "login";

export const useInitiateLogin = (scopelyIdentity: ScopelyIdentity | null) => {
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();

	return async (origPath: string) => {
		if (!scopelyIdentity) {
			console.error("Scopely Identity is not initialized");
			return;
		}

		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "login_option_login_start",
			event_type: "login",
			action: "login_option_login_start",
			login_type: LoginType.Scopely
		});
		setScopelyStateToSession(
			JSON.stringify({ action: LOGIN_ACTION, path: origPath })
		);
		await scopelyIdentity.login({
			//state: encodedState,
			operation: LoginIdentityOperation.Login
			//identityProvider: "optional-idp" // For social login (optional)
		});
	};

	// User is redirected to identity provider...
};

export const useHandleRedirect = ({
	scopelyIdentity,
	setSIDAccessToken,
	setSIDIdentityToken,
	setScopelyIdentityToken
}: {
	scopelyIdentity: ScopelyIdentity | null;
	setSIDAccessToken: Dispatch<SetStateAction<string | null>>;
	setSIDIdentityToken: Dispatch<SetStateAction<string | null>>;
	setScopelyIdentityToken: Dispatch<SetStateAction<string | null>>;
}) => {
	const { reportPlaygamiEvent } = useGoogleAnalyticsContext();

	return async (onSuccess?: () => void) => {
		if (!scopelyIdentity) {
			console.error("Scopely Identity is not initialized");
			return null;
		}

		const currentLocation = window.location.href;
		// Parse the authorization URI
		const authUri = AuthorizationUri.parse(currentLocation) || { state: "" };
		// Get operation type
		const operation = scopelyIdentity.getOperationFromState(authUri.state);

		removeQueryParamsFromCurrentUrl("state");
		// Complete authorization
		const result = await scopelyIdentity.authorizationResult(
			currentLocation,
			operation as LoginIdentityOperation
		);

		if (result.isSuccessful()) {
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "login_option_login_success",
				event_type: "login",
				action: "login_option_login_success",
				login_type: LoginType.Scopely
			});
			const response = result.getResponse();
			const accessToken = response?.getAccessToken();
			const identityToken = response?.getIdToken();

			if (accessToken) {
				setSIDAccessToken(accessToken);
			}
			if (identityToken) {
				setSIDIdentityToken(identityToken);
				setScopelyIdentityToken(identityToken);
			}
			onSuccess && onSuccess();
			// Send token to backend for session creation
			//await sendTokenToBackend(response?.getAccessToken());
		} else {
			console.error("Authorization failed:", result.getError());
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "login_option_login_failure",
				event_type: "login",
				action: "login_option_login_failure",
				login_type: LoginType.Scopely,
				error_type: result.getError() ?? ""
			});
		}
		return result;
	};
};

export const useStartLogin = ({
	scopelyId,
	debug
}: {
	scopelyId: ScopelyId;
	debug: IDebugLogger;
}) => {
	const { loginGoogleAnalyticsHelper, reportPlaygamiEvent } =
		useGoogleAnalyticsContext();
	return async (origPath: string) => {
		loginGoogleAnalyticsHelper.reportLoginOptionLoginStart(LoginType.Scopely);
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "login_option_login_start",
			event_type: "login",
			action: "login_option_login_start",
			login_type: LoginType.Scopely
		});
		debug.logFunction({
			iconText: "/ScopelyIdentityProvider/api/login:",
			message: ["startLogin"]
		});
		setScopelyStateToSession(
			JSON.stringify({ action: LOGIN_ACTION, path: origPath })
		);
		await scopelyId.login({
			operation: LoginIdentityOperation.Login
		});
		//await scopelyId.login(LOGIN_ACTION);
	};
};

export const useTryConcludeLogin = ({
	scopelyId,
	setSIDAccessToken,
	setSIDIdentityToken,
	debug
}: {
	scopelyId: ScopelyId;
	setSIDAccessToken: Dispatch<SetStateAction<string | null>>;
	setSIDIdentityToken: Dispatch<SetStateAction<string | null>>;
	debug: IDebugLogger;
}) => {
	const { loginGoogleAnalyticsHelper, reportPlaygamiEvent } =
		useGoogleAnalyticsContext();
	return async (onSuccess?: () => void) => {
		const loginResult = await scopelyId.authorizationResult(
			window.location.href
		);

		if (loginResult.isSuccessful()) {
			loginGoogleAnalyticsHelper.reportLoginOptionLoginSuccess(
				LoginType.Scopely
			);
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "login_option_login_success",
				event_type: "login",
				action: "login_option_login_success",
				login_type: LoginType.Scopely
			});
			const accessToken = loginResult.getResponse()?.getAccessToken();
			const identityToken = loginResult.getResponse()?.getIdToken();
			debug.log({
				iconText: "ScopelyIdentityProvider.api.login",
				message: [
					"\naccessToken: " + accessToken,
					"\nidentityToken: " + identityToken
				]
			});
			if (accessToken) {
				setSIDAccessToken(accessToken);
			}
			if (identityToken) {
				setSIDIdentityToken(identityToken);
			}
			onSuccess && onSuccess();
		} else {
			debug.log({
				iconText: "Scopely Login Fail",
				message: [loginResult.getError()]
			});
			loginGoogleAnalyticsHelper.reportLoginOptionLoginFailure(
				LoginType.Scopely,
				loginResult.getError() ?? ""
			);
			reportPlaygamiEvent("tp.web_frontend", {
				event_name: "login_option_login_failure",
				event_type: "login",
				action: "login_option_login_failure",
				login_type: LoginType.Scopely,
				error_type: loginResult.getError() ?? ""
			});
		}
		return loginResult;
	};
};
